import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { CartService } from './cart.service';
import { MenuService } from './menu.service';
import { ModalService } from './modal.service';

@Injectable({ providedIn: 'root' })
export class AppNavigationService {
  private router = inject(Router);
  private menuService = inject(MenuService);
  private cartService = inject(CartService);
  private modalService = inject(ModalService);

  // Clean URL segments (without query parameters)
  public currentSegments = signal<string[]>([]);

  // Synchronous computed back target URL
  public backTarget = computed<string[] | null>(() => {
    const segments = this.currentSegments();
    if (segments.length <= 1) {
      // Level 0: /:customerID or root -> No back button
      return null;
    }

    if (segments.length === 2) {
      // Level 1: /:customerID/:menuID -> Return to /:customerID
      return ['/', segments[0]];
    }

    if (segments.length === 3) {
      // Level 2: /:customerID/:menuID/:categoryID
      const catID = segments[2];
      const category = this.menuService.getCategoryById(catID);
      if (category?.ParentCategoryID) {
        // Sub-category -> Return to parent category
        return ['/', segments[0], segments[1], category.ParentCategoryID];
      }
      // Root category -> Return to category list
      return ['/', segments[0], segments[1]];
    }

    if (segments.length === 4) {
      // Level 3: /:customerID/:menuID/:categoryID/:productID -> Return to category/product list
      return ['/', segments[0], segments[1], segments[2]];
    }

    return null;
  });

  public canGoBack = computed(() => this.backTarget() !== null);

  private isProgrammaticNavigating = false;
  private historyTrapPushed = false;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateSegmentsFromUrl(event.urlAfterRedirects);
        this.ensureHistoryTrap();
      });

    this.updateSegmentsFromUrl(this.router.url || window.location.pathname);
    this.initPopstateInterception();
  }

  private updateSegmentsFromUrl(url: string) {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/').filter((p) => p.length > 0);
    this.currentSegments.set(parts);
  }

  public async goBack(isPopstate = false): Promise<void> {
    const segments = this.currentSegments();
    if (segments.length <= 1) return;

    this.isProgrammaticNavigating = true;

    try {
      let target = this.backTarget();

      // If at Level 2 (category) and category wasn't cached yet, fetch it dynamically
      if (segments.length === 3) {
        const catID = segments[2];
        let category = this.menuService.getCategoryById(catID);
        if (!category) {
          category = (await this.menuService.ensureCategory(catID)) || undefined;
        }
        if (category?.ParentCategoryID) {
          target = ['/', segments[0], segments[1], category.ParentCategoryID];
        } else {
          target = ['/', segments[0], segments[1]];
        }
      }

      if (target) {
        await this.router.navigate(target, {
          replaceUrl: isPopstate,
          queryParamsHandling: 'preserve',
        });
      }
    } finally {
      setTimeout(() => {
        this.isProgrammaticNavigating = false;
      }, 150);
    }
  }

  private initPopstateInterception() {
    window.addEventListener('popstate', () => {
      if (this.isProgrammaticNavigating) {
        return;
      }

      // Check if any modal is open; if so, close it and prevent leaving the page
      if (this.modalService.isOpen()) {
        this.modalService.close();
        window.history.pushState({ appTrap: true }, '', window.location.href);
        return;
      }
      if (this.cartService.isCartOpen()) {
        this.cartService.closeCart();
        window.history.pushState({ appTrap: true }, '', window.location.href);
        return;
      }
      if (this.cartService.isTrackingOpen()) {
        this.cartService.closeTracking();
        window.history.pushState({ appTrap: true }, '', window.location.href);
        return;
      }
      if (this.cartService.pendingTableSwitch()) {
        this.cartService.pendingTableSwitch.set(null);
        window.history.pushState({ appTrap: true }, '', window.location.href);
        return;
      }

      // If we can go back hierarchically, intercept the browser's pop and navigate up
      if (this.canGoBack()) {
        this.goBack(true);
      }
    });
  }

  private ensureHistoryTrap() {
    if (this.canGoBack() && !this.historyTrapPushed) {
      window.history.pushState({ appTrap: true }, '', window.location.href);
      this.historyTrapPushed = true;
    }
  }
}
